import { useState, useEffect } from 'react';
import ICAL from 'ical.js';
import { apiFetch } from '../api';

function AirbnbAlerts({ bookingData, refreshKey }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchReservations() {
      try {
        const res = await apiFetch('/api/calendar/airbnb');
        const text = await res.text();
        const parsed = ICAL.parse(text);
        const comp = new ICAL.Component(parsed);
        const vevents = comp.getAllSubcomponents('vevent');

        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const reservations = vevents
          .map((ve) => {
            const event = new ICAL.Event(ve);
            const summary = event.summary || '';
            return {
              id: event.uid || '',
              summary,
              checkIn: event.startDate.toJSDate(),
              checkOut: event.endDate.toJSDate(),
            };
          })
          .filter((r) => r.summary !== 'Airbnb (Not available)')
          .sort((a, b) => a.checkIn - b.checkIn);

        const newAlerts = [];

        reservations.forEach((res) => {
          const checkIn = new Date(res.checkIn);
          checkIn.setHours(0, 0, 0, 0);
          const checkOut = new Date(res.checkOut);
          checkOut.setHours(0, 0, 0, 0);
          const guestName = bookingData[res.id]?.name || res.summary || 'Guest';

          // Check-in today
          if (checkIn.getTime() === now.getTime()) {
            newAlerts.push({
              type: 'checkin',
              title: `${guestName} arrives today`,
              icon: 'checkin',
            });
          }

          // Check-in tomorrow
          if (checkIn.getTime() === tomorrow.getTime()) {
            newAlerts.push({
              type: 'checkin-tomorrow',
              title: `${guestName} arrives tomorrow`,
              icon: 'checkin',
            });
          }

          // Check-out today
          if (checkOut.getTime() === now.getTime()) {
            newAlerts.push({
              type: 'checkout',
              title: `${guestName} checks out today`,
              icon: 'checkout',
            });
          }

          // Check-out tomorrow
          if (checkOut.getTime() === tomorrow.getTime()) {
            newAlerts.push({
              type: 'checkout-tomorrow',
              title: `${guestName} checks out tomorrow`,
              icon: 'checkout',
            });
          }
        });

        // Check for cleaning gaps (checkout today or tomorrow with a future check-in)
        reservations.forEach((res, i) => {
          const checkOut = new Date(res.checkOut);
          checkOut.setHours(0, 0, 0, 0);

          if (checkOut.getTime() === now.getTime() || checkOut.getTime() === tomorrow.getTime()) {
            // Find next reservation
            const next = reservations.find((r) => {
              const nextIn = new Date(r.checkIn);
              nextIn.setHours(0, 0, 0, 0);
              return nextIn >= checkOut;
            });

            if (next) {
              const nextIn = new Date(next.checkIn);
              nextIn.setHours(0, 0, 0, 0);
              const gapDays = Math.round((nextIn - checkOut) / (1000 * 60 * 60 * 24));

              if (gapDays === 0) {
                newAlerts.push({
                  type: 'cleaning-urgent',
                  title: 'Same-day turnover! Clean ASAP',
                  icon: 'cleaning',
                });
              } else if (gapDays === 1) {
                newAlerts.push({
                  type: 'cleaning',
                  title: 'Cleaning needed - next guest tomorrow',
                  icon: 'cleaning',
                });
              } else if (gapDays <= 3) {
                newAlerts.push({
                  type: 'cleaning-upcoming',
                  title: `Cleaning needed - next guest in ${gapDays} days`,
                  icon: 'cleaning',
                });
              }
            }
          }
        });

        // Currently hosting
        reservations.forEach((res) => {
          const checkIn = new Date(res.checkIn);
          checkIn.setHours(0, 0, 0, 0);
          const checkOut = new Date(res.checkOut);
          checkOut.setHours(0, 0, 0, 0);
          const guestName = bookingData[res.id]?.name || res.summary || 'Guest';

          if (now >= checkIn && now < checkOut) {
            const daysLeft = Math.round((checkOut - now) / (1000 * 60 * 60 * 24));
            newAlerts.push({
              type: 'hosting',
              title: `${guestName} staying (${daysLeft} night${daysLeft !== 1 ? 's' : ''} left)`,
              icon: 'hosting',
            });
          }
        });

        setAlerts(newAlerts);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchReservations();
  }, [refreshKey, bookingData]);

  if (loading) return <section className="card"><h2>Airbnb Alerts</h2><p className="loading">Loading...</p></section>;

  return (
    <section className="card alerts-section">
      <h2>Airbnb Alerts</h2>
      {alerts.length === 0 ? (
        <p className="empty">No alerts right now</p>
      ) : (
        <div className="alerts-list">
          {alerts.map((alert, i) => (
            <div key={i} className={`alert-item alert-${alert.type}`}>
              <span className="alert-icon">
                {alert.icon === 'checkin' && '\uD83D\uDEEC'}
                {alert.icon === 'checkout' && '\uD83D\uDEEB'}
                {alert.icon === 'cleaning' && '\uD83E\uDDF9'}
                {alert.icon === 'hosting' && '\uD83C\uDFE0'}
              </span>
              <span className="alert-text">{alert.title}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default AirbnbAlerts;
